/**
 * Creation Relationships Manager (ISSUE-154)
 * 
 * Manages creation relationships between prime artifacts.
 * Shows what artifacts this artifact can create and what can create this artifact.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  message,
  Space,
  Tag,
  Popconfirm,
  Empty,
  Spin,
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  Tooltip,
  Tabs,
  Typography,
  Alert
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import {
  PrimeArtifact,
  PrimeCreationRelationship,
  CreateCreationRelationship,
  UpdateCreationRelationship
} from '../../types/primeArtifact';
import styles from './CreationRelationshipsManager.module.css';

const { Text, Paragraph } = Typography;
const { Option } = Select;

interface CreationRelationshipsManagerProps {
  artifactId: string;
  artifactName?: string;
  onUpdate?: () => void;
}

export const CreationRelationshipsManager: React.FC<CreationRelationshipsManagerProps> = ({
  artifactId,
  artifactName,
  onUpdate
}) => {
  const [loading, setLoading] = useState(true);
  const [relationships, setRelationships] = useState<PrimeCreationRelationship[]>([]);
  const [createdByArtifacts, setCreatedByArtifacts] = useState<PrimeArtifact[]>([]);
  const [availableArtifacts, setAvailableArtifacts] = useState<PrimeArtifact[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState<PrimeCreationRelationship | null>(null);
  const [saving, setSaving] = useState(false);
  const [suggestKeysEnabled, setSuggestKeysEnabled] = useState(false);
  const [lastSuggestedKeys, setLastSuggestedKeys] = useState<{
    relationship_key?: string;
    inverse_key?: string;
  }>({});
  const [form] = Form.useForm();
  const watchedCreatedArtifactId = Form.useWatch('created_artifact_id', form);

  const toSnakeCase = (value: string): string => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_+/g, '_');
  };

  const pluralize = (word: string): string => {
    const w = String(word || '').trim();
    if (!w) return w;
    if (w.endsWith('s')) return w;
    if (w.endsWith('y') && w.length > 1 && !'aeiou'.includes(w[w.length - 2])) return `${w.slice(0, -1)}ies`;
    if (w.endsWith('x') || w.endsWith('ch') || w.endsWith('sh')) return `${w}es`;
    return `${w}s`;
  };

  const getCreatedArtifactName = (createdArtifactId?: string): string => {
    if (!createdArtifactId) return '';
    const found = availableArtifacts.find(a => a.id === createdArtifactId);
    return found?.name || editingRelationship?.created_artifact_name || '';
  };

  const computeSuggestedKeys = (createdArtifactId?: string): { relationship_key: string; inverse_key: string } | null => {
    if (!createdArtifactId) return null;

    const creatorName = artifactName || 'creator';
    const createdName = getCreatedArtifactName(createdArtifactId) || 'created';

    const creator = toSnakeCase(creatorName);
    const created = toSnakeCase(createdName);

    const relationship_key = `${creator}_${pluralize(created)}`;
    const inverse_key = `${created}_${pluralize(creator)}`;

    return { relationship_key, inverse_key };
  };

  const maybeApplySuggestedKeys = (createdArtifactId?: string): void => {
    if (!suggestKeysEnabled) return;

    const suggested = computeSuggestedKeys(createdArtifactId);
    if (!suggested) return;

    const currentRelationshipKey: string | undefined = form.getFieldValue('relationship_key');
    const currentInverseKey: string | undefined = form.getFieldValue('inverse_key');

    const shouldUpdateRelationshipKey =
      !currentRelationshipKey || currentRelationshipKey === lastSuggestedKeys.relationship_key;
    const shouldUpdateInverseKey =
      !currentInverseKey || currentInverseKey === lastSuggestedKeys.inverse_key;

    if (shouldUpdateRelationshipKey || shouldUpdateInverseKey) {
      form.setFieldsValue({
        ...(shouldUpdateRelationshipKey ? { relationship_key: suggested.relationship_key } : {}),
        ...(shouldUpdateInverseKey ? { inverse_key: suggested.inverse_key } : {})
      });
      setLastSuggestedKeys(suggested);
    }
  };

  // When "Suggest keys" is enabled, auto-apply suggestions as soon as we have a target artifact.
  // This covers the "check first, select target later" flow (and edit mode where target is hidden).
  useEffect(() => {
    if (!suggestKeysEnabled) return;
    const createdId = String(watchedCreatedArtifactId || editingRelationship?.created_artifact_id || '');
    if (!createdId) return;
    maybeApplySuggestedKeys(createdId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    suggestKeysEnabled,
    watchedCreatedArtifactId,
    editingRelationship?.created_artifact_id,
    artifactName,
    availableArtifacts
  ]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      console.log('[CreationRelationshipsManager] Loading data for artifact:', artifactId);
      
      // Load creation relationships (what this artifact creates)
      const [relResponse, createdByResponse, artifactsResponse] = await Promise.all([
        PrimeArtifactService.getCreationRelationships(artifactId).catch((err) => {
          console.error('[CreationRelationshipsManager] Error fetching creation relationships:', err);
          return [];
        }),
        PrimeArtifactService.getCreatedBy(artifactId).catch((err) => {
          console.error('[CreationRelationshipsManager] Error fetching created-by relationships:', err);
          return [];
        }),
        PrimeArtifactService.getAll().catch((err) => {
          console.error('[CreationRelationshipsManager] Error fetching all artifacts:', err);
          return [];
        })
      ]);

      console.log('[CreationRelationshipsManager] Loaded data:', {
        relationships: relResponse,
        relationshipsCount: Array.isArray(relResponse) ? relResponse.length : 'N/A',
        relationshipsType: typeof relResponse,
        relationshipsIsArray: Array.isArray(relResponse),
        createdByCount: Array.isArray(createdByResponse) ? createdByResponse.length : 'N/A',
        createdByResponse: createdByResponse,
        createdBySample: Array.isArray(createdByResponse) && createdByResponse.length > 0 ? createdByResponse[0] : 'N/A',
        artifactsCount: Array.isArray(artifactsResponse) ? artifactsResponse.length : 'N/A'
      });

      // Ensure all responses are arrays (defensive check for API inconsistencies)
      const relationshipsArray = Array.isArray(relResponse) ? relResponse : [];
      setRelationships(relationshipsArray);
      setCreatedByArtifacts(Array.isArray(createdByResponse) ? createdByResponse : []);
      // Filter out current artifact from available targets
      const artifacts = Array.isArray(artifactsResponse) ? artifactsResponse : [];
      setAvailableArtifacts(artifacts.filter(a => a.id !== artifactId));
      
      console.log('[CreationRelationshipsManager] State updated:', {
        relationshipsSet: relationshipsArray.length,
        availableArtifactsCount: artifacts.filter(a => a.id !== artifactId).length
      });
    } catch (error: any) {
      console.error('[CreationRelationshipsManager] Error loading creation relationships:', error);
      message.error('Failed to load creation relationships');
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleAddRelationship = () => {
    setEditingRelationship(null);
    setSuggestKeysEnabled(false);
    setLastSuggestedKeys({});
    form.resetFields();
    form.setFieldsValue({
      is_required: false,
      is_recursive: false
    });
    setModalVisible(true);
  };

  const handleEditRelationship = (relationship: PrimeCreationRelationship) => {
    setEditingRelationship(relationship);
    setSuggestKeysEnabled(false);
    setLastSuggestedKeys({});
    form.setFieldsValue({
      created_artifact_id: relationship.created_artifact_id,
      relationship_key: relationship.relationship_key,
      is_required: relationship.is_required,
      is_recursive: relationship.is_recursive,
      description: relationship.creation_context?.description,
      inverse_key: relationship.creation_context?.inverse_key
    });
    setModalVisible(true);
  };

  const handleDeleteRelationship = async (relationshipId: string) => {
    try {
      await PrimeArtifactService.deleteCreationRelationship(artifactId, relationshipId);
      message.success('Relationship deleted successfully');
      await loadData();
      onUpdate?.();
    } catch (error: any) {
      console.error('Error deleting relationship:', error);
      message.error('Failed to delete relationship');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const data: CreateCreationRelationship | UpdateCreationRelationship = {
        relationship_key: values.relationship_key,
        is_required: values.is_required || false,
        is_recursive: values.is_recursive || false,
        creation_context: {
          description: values.description || undefined,
          inverse_key: values.inverse_key || undefined
        }
      };

      if (editingRelationship) {
        // Update existing
        console.log('[CreationRelationshipsManager] Updating relationship:', {
          artifactId,
          relationshipId: editingRelationship.id,
          data
        });
        await PrimeArtifactService.updateCreationRelationship(
          artifactId,
          editingRelationship.id,
          data as UpdateCreationRelationship
        );
        message.success('Relationship updated successfully');
      } else {
        // Create new
        const createData = {
          ...data,
          created_artifact_id: values.created_artifact_id
        } as CreateCreationRelationship;
        
        console.log('[CreationRelationshipsManager] Creating relationship:', {
          artifactId,
          formValues: values,
          preparedData: createData,
          created_artifact_id: values.created_artifact_id,
          hasCreatedArtifactId: !!values.created_artifact_id,
          dataJSON: JSON.stringify(createData, null, 2)
        });
        
        await PrimeArtifactService.createCreationRelationship(artifactId, createData);
        message.success('Relationship created successfully');
      }

      setModalVisible(false);
      await loadData();
      onUpdate?.();
    } catch (error: any) {
      console.error('[CreationRelationshipsManager] Error saving relationship:', {
        error,
        response: error.response,
        responseData: error.response?.data,
        responseDetail: error.response?.data?.detail,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      message.error(error.response?.data?.detail || 'Failed to save relationship');
    } finally {
      setSaving(false);
    }
  };

  const canCreateColumns = [
    {
      title: 'Target Artifact',
      dataIndex: 'created_artifact_name',
      key: 'created_artifact_name',
      render: (name: string, record: PrimeCreationRelationship) => (
        <Space>
          <LinkOutlined />
          <Text strong>{name || record.created_artifact_id}</Text>
        </Space>
      )
    },
    {
      title: 'Relationship Key',
      dataIndex: 'relationship_key',
      key: 'relationship_key',
      render: (key: string) => <Tag color="blue">{key}</Tag>
    },
    {
      title: 'Flags',
      key: 'flags',
      render: (_: any, record: PrimeCreationRelationship) => (
        <Space>
          {record.is_required && <Tag color="red">Required</Tag>}
          {record.is_recursive && (
            <Tooltip title="This artifact can create instances of itself">
              <Tag color="purple" icon={<SyncOutlined />}>Recursive</Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Description',
      key: 'description',
      render: (_: any, record: PrimeCreationRelationship) => (
        <Text type="secondary">
          {record.creation_context?.description || '-'}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: PrimeCreationRelationship) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditRelationship(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this relationship?"
            description="This will remove the creation relationship."
            onConfirm={() => handleDeleteRelationship(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const createdByColumns = [
    {
      title: 'Creator Artifact',
      key: 'creator',
      render: (_: any, record: PrimeArtifact) => {
        // Defensive: try multiple possible field names for artifact name
        const recordAny = record as any;
        const artifactName = record?.name || recordAny?.artifact_name || recordAny?.creator_artifact_name || record?.id || 'Unknown';
        return (
          <Space>
            <ArrowLeftOutlined />
            <Text strong>{artifactName}</Text>
          </Space>
        );
      }
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc: string) => (
        <Text type="secondary">{desc || '-'}</Text>
      )
    }
  ];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large">
          <div style={{ padding: 50, textAlign: 'center', color: '#888' }}>
            Loading creation relationships...
          </div>
        </Spin>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'can-create',
      label: (
        <Space>
          <ArrowRightOutlined />
          Can Create ({relationships.length})
        </Space>
      ),
      children: (
        <div className={styles.tabContent}>
          <div className={styles.sectionHeader}>
            <div>
              <Text type="secondary">
                Artifacts that <Text strong>{artifactName || 'this artifact'}</Text> can create
              </Text>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddRelationship}
            >
              Add Relationship
            </Button>
          </div>

          {relationships.length === 0 ? (
            <Empty
              description="No creation relationships defined"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={handleAddRelationship}>
                Add First Relationship
              </Button>
            </Empty>
          ) : (
            <Table
              dataSource={relationships}
              columns={canCreateColumns}
              rowKey="id"
              pagination={false}
              size="middle"
            />
          )}
        </div>
      )
    },
    {
      key: 'created-by',
      label: (
        <Space>
          <ArrowLeftOutlined />
          Created By ({createdByArtifacts.length})
        </Space>
      ),
      children: (
        <div className={styles.tabContent}>
          <div className={styles.sectionHeader}>
            <Text type="secondary">
              Artifacts that can create <Text strong>{artifactName || 'this artifact'}</Text>
            </Text>
          </div>

          {createdByArtifacts.length === 0 ? (
            <Empty
              description="No artifacts can create this artifact"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Table
              dataSource={createdByArtifacts}
              columns={createdByColumns}
              rowKey="id"
              pagination={false}
              size="middle"
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <Card
        title={
          <Space>
            <LinkOutlined />
            Creation Relationships
          </Space>
        }
        className={styles.card}
      >
        <Alert
          message="Creation Relationships"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              Creation relationships define which artifacts can create other artifacts at runtime.
              When a transition in this artifact creates a new artifact item, it will use these
              relationships to determine valid creation targets.
            </Paragraph>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />

        <Tabs items={tabItems} />
      </Card>

      <Modal
        title={editingRelationship ? 'Edit Creation Relationship' : 'Add Creation Relationship'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          {editingRelationship && (
            <Form.Item name="created_artifact_id" hidden>
              <Input />
            </Form.Item>
          )}
          {!editingRelationship && (
            <Form.Item
              name="created_artifact_id"
              label="Target Artifact"
              rules={[{ required: true, message: 'Please select a target artifact' }]}
              extra="The artifact that this artifact can create"
            >
              <Select
                showSearch
                placeholder="Select an artifact"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.label || '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => {
                  // Only apply suggestions when enabled, and only if fields are empty / still suggested values
                  maybeApplySuggestedKeys(String(value));
                }}
              >
                {availableArtifacts.map(artifact => (
                  <Option key={artifact.id} value={artifact.id} label={artifact.name}>
                    {artifact.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item>
            <Checkbox
              checked={suggestKeysEnabled}
              onChange={(e) => {
                const enabled = e.target.checked;
                setSuggestKeysEnabled(enabled);
                setLastSuggestedKeys({});
                if (enabled) {
                  const createdId = String(form.getFieldValue('created_artifact_id') || editingRelationship?.created_artifact_id || '');
                  if (createdId) {
                    maybeApplySuggestedKeys(createdId);
                  }
                }
              }}
            >
              <Tooltip title="Auto-suggest relationship_key and inverse_key using artifact names. You can still edit them manually.">
                Suggest keys
              </Tooltip>
            </Checkbox>
            {suggestKeysEnabled && !String(form.getFieldValue('created_artifact_id') || editingRelationship?.created_artifact_id || '') && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">Select a target artifact to generate suggested keys.</Text>
              </div>
            )}
          </Form.Item>

          <Form.Item
            name="relationship_key"
            label="Relationship Key"
            rules={[
              { required: true, message: 'Please enter a relationship key' },
              { pattern: /^[a-z_]+$/, message: 'Use lowercase letters and underscores only' }
            ]}
            extra="A unique key to identify this relationship (e.g., subscription_features)"
          >
            <Input placeholder="e.g., subscription_features" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea
              rows={2}
              placeholder="Describe the relationship (optional)"
            />
          </Form.Item>

          <Form.Item
            name="inverse_key"
            label="Inverse Relationship Key"
            extra="Key for looking up this relationship from the created artifact's perspective"
          >
            <Input placeholder="e.g., feature_subscriptions" />
          </Form.Item>

          <Space size="large">
            <Form.Item
              name="is_required"
              valuePropName="checked"
            >
              <Checkbox>
                <Tooltip title="If checked, the creator must create this artifact">
                  Required
                </Tooltip>
              </Checkbox>
            </Form.Item>

            <Form.Item
              name="is_recursive"
              valuePropName="checked"
            >
              <Checkbox>
                <Tooltip title="If checked, this artifact can create instances of itself (e.g., Product → Product)">
                Allow Multiples (Recursive)
                </Tooltip>
              </Checkbox>
            </Form.Item>
          </Space>

          {form.getFieldValue('is_recursive') && (
            <Alert
              message="Recursive Relationship"
              description="This artifact will be able to create new instances of itself. This is useful for hierarchical structures like Product → Sub-Product."
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default CreationRelationshipsManager;
